import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import faqService from '../services/faqService';
import { useToast } from './Toast';

const FAQSources = () => {
  const { showToast } = useToast();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [question, setQuestion] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [chatMode, setChatMode] = useState(false);
  const [chatResponse, setChatResponse] = useState(null);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const data = await faqService.listSources();
      setSources(data.sources || []);
    } catch (error) {
      showToast('Failed to load FAQ sources', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addSource = async (e) => {
    e.preventDefault();
    if (!newSourceUrl.trim()) return;

    try {
      await faqService.addSource(newSourceUrl);
      showToast('FAQ source added successfully', 'success');
      setNewSourceUrl('');
      fetchSources();
    } catch (error) {
      showToast('Failed to add FAQ source', 'error');
    }
  };

  const deleteSource = async (sourceUrl) => {
    if (!window.confirm('Are you sure you want to delete this FAQ source?')) return;

    try {
      await faqService.deleteSource(sourceUrl);
      showToast('FAQ source deleted', 'success');
      fetchSources();
    } catch (error) {
      showToast('Failed to delete FAQ source', 'error');
    }
  };

  const regenerateFAQ = async () => {
    setRegenerating(true);
    try {
      await faqService.regenerate();
      showToast('FAQ regeneration started', 'success');
      setTimeout(fetchSources, 2000);
    } catch (error) {
      showToast('Failed to regenerate FAQ', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const searchFAQ = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setSearching(true);
    setChatResponse(null);
    setSearchResults(null);
    
    try {
      if (chatMode) {
        const data = await faqService.chat(question, 3);
        setChatResponse(data);
      } else {
        const data = await faqService.search(question, 3);
        setSearchResults(data.results || []);
        if (data.results?.length === 0) {
          showToast('No results found', 'info');
        }
      }
    } catch (error) {
      showToast(`Failed to ${chatMode ? 'chat with' : 'search'} FAQ`, 'error');
      if (chatMode) {
        setChatResponse(null);
      } else {
        setSearchResults([]);
      }
    } finally {
      setSearching(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { emoji: '⏳', class: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      processing: { emoji: '🔄', class: 'bg-blue-100 text-blue-800', text: 'Processing' },
      completed: { emoji: '✅', class: 'bg-green-100 text-green-800', text: 'Completed' },
      failed: { emoji: '❌', class: 'bg-red-100 text-red-800', text: 'Failed' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        {badge.emoji} {badge.text}
      </span>
    );
  };

  return (
    <DashboardLayout>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="mb-6">
            <div className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">FAQ Sources</div>
            <p className="text-sm text-gray-600">Manage knowledge base sources for your AI assistant</p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Test FAQ</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600">Search</span>
                <input
                  type="checkbox"
                  checked={chatMode}
                  onChange={(e) => {
                    setChatMode(e.target.checked);
                    setSearchResults(null);
                    setChatResponse(null);
                  }}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="text-sm text-gray-600">Chat (AI)</span>
              </label>
            </div>
            <form onSubmit={searchFAQ} className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask a question..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button type="submit" disabled={searching} className="btn-primary whitespace-nowrap disabled:opacity-50">
                {searching ? '🔍 Searching...' : chatMode ? '💬 Ask AI' : '🔍 Search'}
              </button>
            </form>

            {chatResponse && (
              <div className="mt-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                <h3 className="font-semibold text-gray-900 mb-2">💬 AI Response:</h3>
                <p className="text-gray-800 mb-4 whitespace-pre-wrap">{chatResponse.answer}</p>
                {chatResponse.sources?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs text-gray-600 mb-2">Sources:</p>
                    <div className="space-y-1">
                      {chatResponse.sources.map((source, idx) => (
                        <a
                          key={idx}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-xs text-blue-600 hover:text-blue-800"
                        >
                          🔗 {source.question} ({(source.similarity * 100).toFixed(0)}% match)
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {searchResults && (
              <div className="mt-4 space-y-4">
                {searchResults.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No results found</p>
                ) : (
                  searchResults.map((result, idx) => (
                    <div key={idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 flex-1">{result.question}</h3>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded whitespace-nowrap">
                          {(result.similarity * 100).toFixed(1)}% match
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">
                        {result.answer.length > 300 ? `${result.answer.substring(0, 300)}...` : result.answer}
                      </p>
                      <a 
                        href={result.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        🔗 {result.source_url}
                      </a>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Source</h2>
            <form onSubmit={addSource} className="flex flex-col sm:flex-row gap-3">
              <input
                type="url"
                value={newSourceUrl}
                onChange={(e) => setNewSourceUrl(e.target.value)}
                placeholder="https://example.com/faq"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button type="submit" className="btn-primary whitespace-nowrap">
                Add Source
              </button>
            </form>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Your Sources ({sources.length})</h2>
              <button 
                onClick={regenerateFAQ} 
                disabled={regenerating || sources.length === 0}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regenerating ? '🔄 Regenerating...' : '🔄 Regenerate All'}
              </button>
            </div>

            {sources.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-2">No FAQ sources configured yet.</p>
                <p>Add a source URL above to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sources.map((source, index) => (
                  <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow gap-3">
                    <div className="flex-1 min-w-0">
                      <a 
                        href={source.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium break-all"
                      >
                        {source.source_url}
                      </a>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {getStatusBadge(source.status)}
                        <span className="text-sm text-gray-500">
                          {new Date(source.extraction_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button 
                      onClick={() => deleteSource(source.source_url)}
                      className="sm:ml-4 p-2 text-gray-400 hover:text-red-600 transition-colors self-end sm:self-auto"
                      title="Delete source"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default FAQSources;
